import Class from '@core/Class';
import {formatDate, getNumberOfDaysFromNow} from '@util/DateUtils';
import sortBy from 'ramda/src/sortBy';
import filter from 'ramda/src/filter';
import appLogger from '@core/LoggingProvider';
import Module from '@core/Module';
import View from '@core/View';
import SelectMenu from '../ui/SelectMenu';
import User from '../User';
import assign from 'object-assign';
import orderUtil from '@modules/orders/orderUtil';
import OrderItemFullDetailController from '@modules/orders/orderItemFullDetailController';
import OrdersService from '@services/OrdersService';

const conf = {
    orderHistoryRange: {
        numberOfDays: 546,
        numberOfRows: 150
    },
    filterOptions: {
        90: 'Past 90 days',
        182: 'Past 6 months',
        365: 'Past 12 months',
        546: 'Past 18 months'
    },
    messages: {
        ORDER_HISTORY_PROCESSING: 'Retrieving orders...',
        ORDER_HISTORY_FAILED: 'Sorry, there was a problem on our end and we were unable to retrieve your order information',
        ORDER_HISTORY_PROMISE_REJECT: 'Request for order history list failed',
        ORDER_HISTORY_MISSING_PARAMS: 'getOrderHistoryFor: missing required keys to get order info '
    }
};

var OrderHistoryController = (Class.extend( Module )).extend( {

    defaults: {
        request: OrdersService.getOrderHeaders
    },

    events: {},

    elements: {
        '.odr-history-actions': 'orderActionsContentArea',
        '.odr-list.accordion': 'contentArea'
    },

    init: function ( el, options ) {
        this.options = this.options || {};
        assign( this.options, this.defaults, options || {} );

        this.orderSelect = null;
        this.el = $( el );

        if ( !this.el[0] ) {
            return;
        }

        this.messageBoxView = new View( 'messagebox' );
        this.update_elements();

        this.orders = this.getOrderHistory()
            .then( this.renderOrderHistory.bind( this ) )
    },

    destroy: function () {
        this.el.trigger( 'destroy:accordion' );
    },

    redirectToLogin: function () {
        this.setLoadMessage( 'redirecting', upi.conf.SESSION_TIMEOUT );
        window.setTimeout( User.redirectToLogin, 750 );
    },

    setLoadMessage: function ( type, message ) {
        var messageView = this.messageBoxView.render( {
            // these message bars will always use the slideDown animation
            type: (type || '') + '',
            effect: 'slideDown',
            message: message || ''
        } );

        this.contentArea.html( messageView );
    },

    toggleEmptyMessage: function ( doShow ) {
        this.el.toggleClass( 'is-empty', !!doShow );
    },

    getOrderHistory: function () {
        const {request} = this.options;

        if ( !User.isLogged() ) {
            User.redirectToLogin();
            return Promise.reject();
        }

        this.toggleEmptyMessage( false );
        this.setLoadMessage( 'processing', conf.messages.ORDER_HISTORY_PROCESSING );

        if ( typeof request !== 'function' ) {
            return Promise.reject( conf.messages.ORDER_HISTORY_MISSING_PARAMS + '"request" option' );
        }
        else {
            return request( conf.orderHistoryRange ).catch(
                error => {
                    appLogger.capture( error );
                    let reason = error.reason;

                    switch ( reason ) {
                        // not authenticated
                        case 'noauth':
                            this.redirectToLogin();
                            break;

                        default:
                            this._handleUnknownError();
                    }

                    return Promise.reject();
                }
            );
        }
    },

    showOrdersForDayRange: function ( numDays ) {
        var self = this,
            filteredOrders;

        this.orders.then( function ( {orders} ) {
            var sequence = [];

            filteredOrders =
                filterOrdersByNumberDays( orders, numDays ).map( ( {orderNumber} ) => {
                    return orderNumber && orderNumber.toString();
                } );

            self.orderList.forEach( item => {
                var $item = $( item ),
                    itemId = item.getAttribute( 'data-order-number' ),
                    doShow = filteredOrders.indexOf( itemId.toString() ) > -1;

                sequence.push( {el: $item, doShow: doShow} );
            } );

            var count = 0;

            function toggleItem( item ) {
                count++;
                item.el.toggleClass( 'show', item.doShow );

                setTimeout( function () {
                    if ( count < sequence.length ) {
                        toggleItem( sequence[count] );
                    }
                }, 30 );
            }

            if ( sequence.length ) {
                toggleItem( sequence[0] );
            }
        } );
    },

    renderOrderHistory: function ( {orders} ) {

        // build the template
        var filterOptions;

        if ( orders && orders.length >= 1 ) {
            if ( this.orderSelect ) {
                this.orderSelect.destroy();
            }

            filterOptions = getOrderDateFilterOptions(
                orders,
                conf.filterOptions
            );

            // store ref to <select> to handle cleanup for re-renders
            this.orderSelect = createPastOrderSelect( filterOptions, this.showOrdersForDayRange.bind( this ) );
            this.orderList = createOrderListHtml( sortOrdersByDate( orders ) );
            this.showOrdersForDayRange( this.orderSelect.selectedOpt.value );
            this.contentArea.html( this.orderList );
            this.orderActionsContentArea.html( this.orderSelect.selEl );

            // initialize OrderItemController for each item in order history
            // order items must be in the DOM before initializing Controller
            this.orderList.forEach( el => {
                new OrderItemFullDetailController( el );
            } );

        }
        else {
            this.toggleEmptyMessage( true );
            this.contentArea.empty();
        }

        return {orders};
    },

    _handleUnknownError: function () {
        // todo: attempt 2-3 times, then fail gracefully
        this.setLoadMessage( 'error', conf.messages.ORDER_HISTORY_FAILED );
    }

} );

window.OrderHistoryController = OrderHistoryController;
export default OrderHistoryController;

/****************************
 *  helpers
 *****************************/
/**
 * Format ViewModel to be passed to OrderHistoryItem template
 * @param {Object} order
 * @returns {OrderHeaderDetail}
 */
function serializeOrderHistoryItem( order ) {
    function getTrackingAvailability( order ) {
        // hide tracking tab 90 days after order was placed
        // hide tracking tab if order status: pending, cancelled
        return (
            getNumberOfDaysFromNow( order.orderDate ) < 90 &&
            !/pending|cancelled/gi.test( order.orderStatus )
        );
    }

    return {
        orderDate: formatDate( order.orderDate, 'longNameFull' ),
        poNumber: order.poNumber,
        confirmationOrderNumber: order.orderNumber,
        orderStatus: order.orderStatus.toLowerCase(),
        orderStatusBadgeHtml: orderUtil.renderStatusBadge( order.orderStatus ),
        orderDetailsUrl: `${upi.paths.ORDER_DETAILS_URL}?orderNumber=${order.orderNumber}`,
        showTrackingInfo: getTrackingAvailability( order )
    };
}

function createPastOrderSelect( options, fn = () => {
} ) {
    const el = createSelect( options );
    return new SelectMenu( el, {onChange: fn} );
}

/**
 *
 * @param {Array} options - array of option objects containing <option> label and value
 * @returns {HTMLSelectElement}
 */
function createSelect( options ) {
    var optionHtml = [],
        defaultSet = false,
        select = $( '<select class="cs-select action-control odr-history-actions--select-past">' );

    for ( let key in options ) {
        if ( options.hasOwnProperty( key ) ) {
            optionHtml.push( `
                <option ${defaultSet ? '' : 'selected'} value="${key}">${options[key]}</option>
            ` );
            if ( !defaultSet ) {
                defaultSet = true;
            }
        }
    }

    return select.html( optionHtml.join( '' ) )[0];
}

function createOrderListHtml( orders ) {
    let tmpl = new View( 'orderHistoryItem' );

    return orders
        .map( orderItem => tmpl.render( serializeOrderHistoryItem( orderItem ) ) )
        .map( $ )
        .map( $el => $el[0] );
}

/**
 * Sort a list of orders by date defaulting to descending order
 * @param {Array} orders - an array of Order objects
 * @param dir - direction to sort [descending(default), ascending]
 * @returns {Array} orders
 */
function sortOrdersByDate( orders, dir ) {
    const sortByDate = sortBy( item => new Date( item.orderDate ) );

    if ( dir !== 'ascending' ) {
        return sortByDate( orders ).reverse();
    }

    return sortByDate( orders );
}

const ordersInDateRange = numDaysLimit => order => getNumberOfDaysFromNow( order.orderDate ) < numDaysLimit;

/**
 * Return a filtered list of Order objects starting from a specified number of days and beyond
 * @param {Array} orders - an array of Order objects
 * @returns {Array} orders
 */
function filterOrdersByNumberDays( orders, numDaysLimit ) {
    return filter( ordersInDateRange( numDaysLimit ), orders );
}

/**
 * Get the available sort date options for the current order list.
 * Finds the most recent order in the list and uses the order date to determine the baseline
 * for the available options starting at 90days, 180days, 365days
 * @param {Array} orders - an array of Order objects
 */
function getOrderDateFilterOptions( orders, optionMap ) {
    optionMap = optionMap || {};

    var mostRecentOrder = sortOrdersByDate( orders )[0],
        filteredOptionMap = {},
        daysFromNow = getNumberOfDaysFromNow( mostRecentOrder.orderDate );

    // console.log( 'meta:', orders, daysFromNow, mostRecentOrder.orderDate );
    for ( let key in optionMap ) {
        if ( optionMap.hasOwnProperty( key ) && Math.min( daysFromNow, 546 ) <= key ) {
            filteredOptionMap[key] = optionMap[key];
        }
    }

    // console.log( 'FILTERS:', optionMap );
    return filteredOptionMap;
}
